// Fixed-point math — all game simulation uses this instead of floats
// Values are integers scaled by SCALE (1000 = 3 decimal places of precision)

export type Fixed = number;

const SCALE = 1000;

// Precomputed sin lookup table — 4096 entries, values in fixed-point
// _sinTable[i] = sin(i / 4096 * 2π) * 1000
const SIN_TABLE_SIZE = 4096;
const _sinTable = new Int16Array(SIN_TABLE_SIZE);
for (let i = 0; i < SIN_TABLE_SIZE; i++) {
  _sinTable[i] = Math.round(Math.sin((i / SIN_TABLE_SIZE) * Math.PI * 2) * SCALE);
}

export const FP = {
  SCALE,
  ZERO: 0 as Fixed,
  ONE: SCALE as Fixed,

  from(n: number): Fixed {
    return (n * SCALE) | 0;
  },

  toFloat(a: Fixed): number {
    return a / SCALE;
  },

  add(a: Fixed, b: Fixed): Fixed {
    return (a + b) as Fixed;
  },

  sub(a: Fixed, b: Fixed): Fixed {
    return (a - b) as Fixed;
  },

  mul(a: Fixed, b: Fixed): Fixed {
    return ((a * b / SCALE) | 0) as Fixed;
  },

  div(a: Fixed, b: Fixed): Fixed {
    return ((a * SCALE / b) | 0) as Fixed;
  },

  abs(a: Fixed): Fixed {
    return (a < 0 ? -a : a) as Fixed;
  },

  min(a: Fixed, b: Fixed): Fixed {
    return (a < b ? a : b) as Fixed;
  },

  max(a: Fixed, b: Fixed): Fixed {
    return (a > b ? a : b) as Fixed;
  },

  clamp(val: Fixed, lo: Fixed, hi: Fixed): Fixed {
    return FP.max(lo, FP.min(val, hi));
  },

  // Fixed-point PI
  PI: (3142) as Fixed, // 3.142 * 1000
  TWO_PI: (6283) as Fixed,

  // Trig lookup table — 4096 entries covering 0 to 2π (~0.09° resolution)
  sin(a: Fixed): Fixed {
    const twoPi = 6283;
    let v = ((a % twoPi) + twoPi) % twoPi;
    const idx = (v * SIN_TABLE_SIZE / twoPi) | 0;
    return _sinTable[idx & (SIN_TABLE_SIZE - 1)] as Fixed;
  },

  cos(a: Fixed): Fixed {
    // cos(a) = sin(a + PI/2)
    return FP.sin((a + 1571) as Fixed); // PI/2 = 1571
  },

  // Integer square root (Babylonian method)
  sqrt(a: Fixed): Fixed {
    if (a <= 0) return 0 as Fixed;
    // Scale up to maintain precision: sqrt(a * SCALE) gives correct fixed result
    let x = a * SCALE;
    let y = x;
    let z = (y + 1) >> 1;
    while (z < y) {
      y = z;
      z = (z + (x / z | 0)) >> 1;
    }
    return y as Fixed;
  },
};

// 2D fixed-point vector
export type FPVec2 = { x: Fixed; y: Fixed };

// 3D fixed-point vector
export type FPVec3 = { x: Fixed; y: Fixed; z: Fixed };

export const Vec3 = {
  create(x: Fixed, y: Fixed, z: Fixed): FPVec3 {
    return { x, y, z };
  },

  zero(): FPVec3 {
    return { x: FP.ZERO, y: FP.ZERO, z: FP.ZERO };
  },

  add(a: FPVec3, b: FPVec3): FPVec3 {
    return { x: FP.add(a.x, b.x), y: FP.add(a.y, b.y), z: FP.add(a.z, b.z) };
  },

  sub(a: FPVec3, b: FPVec3): FPVec3 {
    return { x: FP.sub(a.x, b.x), y: FP.sub(a.y, b.y), z: FP.sub(a.z, b.z) };
  },

  scale(v: FPVec3, s: Fixed): FPVec3 {
    return { x: FP.mul(v.x, s), y: FP.mul(v.y, s), z: FP.mul(v.z, s) };
  },

  dot(a: FPVec3, b: FPVec3): Fixed {
    return FP.add(FP.add(FP.mul(a.x, b.x), FP.mul(a.y, b.y)), FP.mul(a.z, b.z));
  },

  cross(a: FPVec3, b: FPVec3): FPVec3 {
    return {
      x: FP.sub(FP.mul(a.y, b.z), FP.mul(a.z, b.y)),
      y: FP.sub(FP.mul(a.z, b.x), FP.mul(a.x, b.z)),
      z: FP.sub(FP.mul(a.x, b.y), FP.mul(a.y, b.x)),
    };
  },

  lengthSq(v: FPVec3): Fixed {
    return Vec3.dot(v, v);
  },

  length(v: FPVec3): Fixed {
    return FP.sqrt(Vec3.lengthSq(v));
  },

  normalize(v: FPVec3): FPVec3 {
    const len = Vec3.length(v);
    if (len === 0) return Vec3.zero();
    return { x: FP.div(v.x, len), y: FP.div(v.y, len), z: FP.div(v.z, len) };
  },
};

export const Vec2 = {
  create(x: Fixed, y: Fixed): FPVec2 {
    return { x, y };
  },

  fromNumbers(x: number, y: number): FPVec2 {
    return { x: FP.from(x), y: FP.from(y) };
  },

  zero(): FPVec2 {
    return { x: FP.ZERO, y: FP.ZERO };
  },

  add(a: FPVec2, b: FPVec2): FPVec2 {
    return { x: FP.add(a.x, b.x), y: FP.add(a.y, b.y) };
  },

  sub(a: FPVec2, b: FPVec2): FPVec2 {
    return { x: FP.sub(a.x, b.x), y: FP.sub(a.y, b.y) };
  },

  scale(v: FPVec2, s: Fixed): FPVec2 {
    return { x: FP.mul(v.x, s), y: FP.mul(v.y, s) };
  },

  lengthSq(v: FPVec2): Fixed {
    return FP.add(FP.mul(v.x, v.x), FP.mul(v.y, v.y));
  },

  length(v: FPVec2): Fixed {
    return FP.sqrt(Vec2.lengthSq(v));
  },

  normalize(v: FPVec2): FPVec2 {
    const len = Vec2.length(v);
    if (len === 0) return Vec2.zero();
    return { x: FP.div(v.x, len), y: FP.div(v.y, len) };
  },

  dot(a: FPVec2, b: FPVec2): Fixed {
    return FP.add(FP.mul(a.x, b.x), FP.mul(a.y, b.y));
  },
};
